import { NgModule } from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'

// Replace individual PrimeNG imports with focused modules
import { CorePrimeNGModule } from './core-primeng.module'
import { FormsPrimeNGModule } from './forms-primeng.module'
import { DataPrimeNGModule } from './data-primeng.module'
import { LayoutPrimeNGModule } from './layout-primeng.module'

// Additional PrimeNG modules not covered by focused modules
import { RippleModule } from 'primeng/ripple'
import { AvatarModule } from 'primeng/avatar'
import { AvatarGroupModule } from 'primeng/avatargroup'
import { FileUploadModule } from 'primeng/fileupload'
import { ImageModule } from 'primeng/image'
// Removed rarely used modules for tree-shaking: BadgeModule, ToggleButtonModule, ScrollerModule
import { ConfirmPopupModule } from 'primeng/confirmpopup'
import { DragDropModule } from 'primeng/dragdrop'
import { MessageModule } from 'primeng/message'
import { TagModule } from 'primeng/tag'
import { SelectButtonModule } from 'primeng/selectbutton'
import { SkeletonModule } from 'primeng/skeleton'

import { NgxSpinnerModule } from 'ngx-spinner'
import { TranslateModule } from '@ngx-translate/core'

import { InteractiveGuideComponent } from './components/interactive-guide/interactive-guide.component'
import { PhotoUploaderComponent } from './components/photo-uploader/photo-uploader.component'
import { AccordionComponent } from './components/accordion/accordion.component'
import { AccordionGroupComponent } from './components/accordion/accordion.group.component'
import { ModalWriteMessageComponent } from './components/modal-write-message/modal-write-message.component'
import { MessageService } from 'primeng/api'
import { AlertsComponent } from './components/alerts/alerts.component'
import { StatsboxComponent } from './components/statsbox/statsbox.component'
import { StatsboxGraphComponent } from './components/statsbox-graph/statsbox-graph.component'

import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TicketUploaderComponent } from './components/ticket-uploader/ticket-uploader.component'

import { IntervalsService } from './services/intervals.service'
import { KnobGaugeComponent } from './components/myknob/myknob.component'

import { TicketUploaderWithIconComponent } from './components/ticket-uploader-with-icon/ticket-uploader-with-icon.component'
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll'
import { CardWrapperComponent } from './components/card-wrapper/card-wrapper.component'
import { ChipsAutocompleteComponent } from './components/chips-autocomplete/chips-autocomplete.component'
import { MyTreeComponent } from './components/myTree/myTree.component'
import { GenerateReportModalComponent } from './components/generate-report-modal/generate-report-modal.component'
import { MenuComponent } from './components/menu/menu.component'
import { StationTypeComponent } from './components/station-type/station-type.component'
import { MobileWithCountryCodeInputComponent } from './components/mobile-with-country-code-input/mobile-with-country-code-input.component'
import { ChipsWithRemainedDataComponent } from './components/chips-with-remained-data/chips-with-remained-data.component'
import { MyDialogComponent } from './components/my-dialog/my-dialog.component'
import { RuleBaseMessageIndexComponent } from './components/rule-based-messages/index/index.component'
import { RuledMessageModalComponent } from './components/rule-based-messages/ruled-message-modal/ruled-message-modal.component'
import { RestrictionsComponent } from './components/restrictions/restrictions.component'
import { ModalInvitationBaseComponent } from './components/modal-invitation-base/modal-invitation-base.component'
import { ListBoxInCardComponent } from './components/list-box-in-card/list-box-in-card.component'
import { ReportHeaderComponent } from './components/report-header/report-header.component'
import { AllowedPermissionsComponent } from './components/allowed-permissions/allowed-permissions.component'
import { StationService } from './services/stations.service'
import { CountriesService } from './pages/settings/services/countries.service'
import { HasPermissionDirective } from '@app/core/auth/directives/has-permission.directive'
import { YabiTableComponent } from './components/yabi-table/yabi-table.component'
import { YabiInputComponent } from './components/yabi-input/yabi-input.component'
import { StepsComponent } from './components/steps/steps.component'
import { TableCellComponent } from './components/table-cell/table-cell.component'
import { EditableTableCellComponent } from './components/editable-table-cell/editable-table-cell.component'
import { InvitationCalendarComponent } from './components/invitation-calendar/invitation-calendar.component'
import { GoogleMapsModule } from '@angular/google-maps'
import { HelpCenterComponent } from './pages/help-center/help-center.component'
import { ApiReferenceComponent } from './pages/help-center/components/api-reference/api-reference.component'
import { UserManualComponent } from './pages/help-center/components/user-manual/user-manual.component'
import { setOptions } from '@googlemaps/js-api-loader'
import { environment } from 'src/config/environments/environment'
import { GoogleMapsService } from './services/google-maps.service'
import { PlaceAutocompleteComponent } from './components/place-autocomplete/place-autocomplete.component'

// Configure Google Maps API
setOptions({
  key: environment.googleMapApiKey,
  libraries: ['places', 'marker'],
  v: 'weekly',
})

import { RequirementSubmissionsComponent } from './pages/requirement-submissions/requirement-submissions.component'
import { ModalRequirementSubmissionFormComponent } from './pages/requirement-submissions/modal-requirement-submission-form/modal-requirement-submission-form.component'
import { FileUploaderComponent } from './components/file-uploader/file-uploader.component'
import { DigitalClockComponent } from './components/digital-clock/digital-clock.component'
import { BookingsComponent } from './pages/bookings/bookings.component'
import { BookingFormComponent } from './pages/bookings/booking-form/booking-form.component'
import { CoreUIModule } from './core-ui.module'
import { CorePipesDirectivesModule } from './core-pipes-directives.module'
import { RoleMembersComponent } from './components/role-members/role-members.component'

@NgModule({
  declarations: [
    InteractiveGuideComponent,
    PhotoUploaderComponent,
    FileUploaderComponent,
    AccordionComponent,
    AccordionGroupComponent,
    ModalWriteMessageComponent,
    AlertsComponent,
    StatsboxComponent,
    StatsboxGraphComponent,
    TicketUploaderComponent,
    HasPermissionDirective,
    PlaceAutocompleteComponent,
    KnobGaugeComponent,
    TicketUploaderWithIconComponent,
    CardWrapperComponent,
    ChipsAutocompleteComponent,
    MyTreeComponent,
    GenerateReportModalComponent,
    MenuComponent,
    StationTypeComponent,
    MobileWithCountryCodeInputComponent,
    ChipsWithRemainedDataComponent,
    MyDialogComponent,
    RuleBaseMessageIndexComponent,
    RuledMessageModalComponent,
    RestrictionsComponent,
    ModalInvitationBaseComponent,
    ListBoxInCardComponent,
    ReportHeaderComponent,
    AllowedPermissionsComponent,
    YabiTableComponent,
    YabiInputComponent,
    StepsComponent,
    TableCellComponent,
    EditableTableCellComponent,
    InvitationCalendarComponent,
    HelpCenterComponent,
    ApiReferenceComponent,
    UserManualComponent,
    RequirementSubmissionsComponent,
    ModalRequirementSubmissionFormComponent,
    DigitalClockComponent,
    BookingsComponent,
    BookingFormComponent,
    RoleMembersComponent,
  ],
  imports: [
    CoreUIModule,
    CorePipesDirectivesModule,

    // Focused PrimeNG modules (replaces 53+ individual imports)
    CorePrimeNGModule,
    FormsPrimeNGModule,
    DataPrimeNGModule,
    LayoutPrimeNGModule,

    // Additional PrimeNG modules not in focused modules
    RippleModule,
    AvatarModule,
    AvatarGroupModule,
    FileUploadModule,
    ConfirmPopupModule,
    DragDropModule,
    MessageModule,
    MessageModule,
    TagModule,
    SelectButtonModule,
    SkeletonModule,

    // Other modules
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgxSpinnerModule,
    TranslateModule,
    GoogleMapsModule,
    DragScrollComponent,
    DragScrollItemDirective,
  ],
  exports: [
    CoreUIModule,
    CorePipesDirectivesModule,
    CommonModule,

    // Export focused PrimeNG modules
    CorePrimeNGModule,
    FormsPrimeNGModule,
    DataPrimeNGModule,
    LayoutPrimeNGModule,

    // Export additional PrimeNG modules
    RippleModule,
    AvatarModule,
    AvatarGroupModule,
    FileUploadModule,
    ImageModule,
    ConfirmPopupModule,
    DragDropModule,
    MessageModule,
    MessageModule,
    TagModule,
    SelectButtonModule,
    SkeletonModule,

    // Export other modules
    NgxSpinnerModule,
    TranslateModule,
    GoogleMapsModule,
    DragScrollComponent,
    DragScrollItemDirective,

    // Export all shared components (cleaned - no duplicates)
    AccordionComponent,
    AccordionGroupComponent,
    AlertsComponent,
    AllowedPermissionsComponent,
    CardWrapperComponent,
    ChipsAutocompleteComponent,
    ChipsWithRemainedDataComponent,
    DigitalClockComponent,
    EditableTableCellComponent,
    FileUploaderComponent,
    GenerateReportModalComponent,
    HasPermissionDirective,
    ApiReferenceComponent,
    UserManualComponent,
    HelpCenterComponent,
    InteractiveGuideComponent,
    InvitationCalendarComponent,
    ListBoxInCardComponent,
    MenuComponent,
    MobileWithCountryCodeInputComponent,
    ModalInvitationBaseComponent,
    ModalRequirementSubmissionFormComponent,
    ModalWriteMessageComponent,
    MyDialogComponent,
    KnobGaugeComponent,
    MyTreeComponent,
    PhotoUploaderComponent,
    PlaceAutocompleteComponent,
    ReportHeaderComponent,
    RoleMembersComponent,
    RequirementSubmissionsComponent,
    BookingsComponent,
    BookingFormComponent,
    RestrictionsComponent,
    RuleBaseMessageIndexComponent,
    RuledMessageModalComponent,
    StationTypeComponent,
    StatsboxComponent,
    StatsboxGraphComponent,
    StepsComponent,
    TableCellComponent,
    TicketUploaderComponent,
    TicketUploaderWithIconComponent,
    YabiInputComponent,
    YabiTableComponent,
  ],
  providers: [MessageService, IntervalsService, CountriesService, DatePipe, StationService, GoogleMapsService],
})
export class SharedModule {}
